import { useNavigate } from 'react-router-dom';
import { Button, EmptyState, t } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { EnvironmentBadges, ObligationBadges } from '../../components/Inventory';
import { IndexList, stateColumn, ViewEmpty } from '../../components/IndexList';
import { useSession } from '../../lib/session';

type Row = maintenance.components['schemas']['ProjectRow'];

/** Projects (SPEC-005): « Tout » by default, one view per state (D54); « Créer un projet » for operators and administrators. */
export function ProjectsPage() {
  const { enums, can } = useSession();
  const navigate = useNavigate();
  const label = (list: { value: string; label: string }[], value: string) => list.find(i => i.value === value)?.label ?? value;
  const create = can('project.create')
    ? <Button icon="add" onClick={() => navigate('/projects/new')}>{t('projects.create')}</Button>
    : undefined;
  return (
    <IndexList<Row>
      id="projects"
      title={t('projects.title')}
      subtitle={() => t('projects.subtitle')}
      actions={create}
      search={t('projects.search')}
      rowHref={r => `/projects/${encodeURIComponent(r.name)}`}
      columns={[
        stateColumn<Row>(enums),
        { key: 'name', label: t('projects.columns.name'), pinned: 'left', width: 180, minWidth: 140, sortable: true, render: r => r.title },
        { key: 'client', label: t('projects.columns.client'), width: 150, grow: true, sortable: true, render: r => r.clientTitle },
        { key: 'applications', label: t('projects.columns.applications'), align: 'right', width: 120 },
        { key: 'environments', label: t('projects.columns.environments'), width: 360, render: r => <EnvironmentBadges environments={r.environments} /> },
        { key: 'servers', label: t('projects.columns.servers'), align: 'right', width: 110, sortable: true, render: r => (r.servers > 0 && r.dedicatedServers === 0 ? t('projects.sharedOnly') : String(r.servers)) },
        { key: 'obligations', label: t('projects.columns.obligations'), width: 150, render: r => <ObligationBadges obligations={r.obligations} /> },
        { key: 'criticality', label: t('projects.columns.criticality'), width: 120, sortable: true, render: r => label(enums.criticalities, r.criticality) },
        { key: 'team', label: t('projects.columns.team'), hidden: true },
      ]}
      facets={[
        { key: 'state', label: t('inventory.filters.state') },
        { key: 'client', label: t('inventory.filters.client') },
        { key: 'criticality', label: t('inventory.filters.criticality'), valueLabel: v => label(enums.criticalities, v) },
        { key: 'obligation', label: t('inventory.filters.obligation'), valueLabel: v => label(enums.obligations, v) },
        { key: 'team', label: t('inventory.filters.team') },
      ]}
      emptyFor={(view, setView, data) =>
        data && data.outOf === 0
          ? <EmptyState framed={false} icon="folder" title={t('projects.empty.catalogue')} action={create} />
          : view !== 'all'
            ? <ViewEmpty list="projects" view={view} icon="folder" onSeeAll={() => setView('all')} />
            : <EmptyState framed={false} icon="folder" title={t('inventory.noResult')} />}
    />
  );
}
